import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { RefObject, useRef } from 'react';
import { Spinner, UncontrolledTooltip } from 'reactstrap';

import { variantClass } from './AppButton';

// An icon-only button. Its label is its accessible name and its tooltip (OQ-74, OQ-181).
type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  icon: IconProp;
  label: string;
  tooltip?: string;
  tooltipPosition?: 'left' | 'right' | 'top' | 'bottom';
  variant?: 'secondary' | 'ghost' | 'danger-ghost' | 'inverse';
  size?: 'sm';
  loading?: boolean;
};

export const IconButton = ({
  icon,
  label,
  tooltip,
  tooltipPosition = 'top',
  variant = 'secondary',
  size,
  loading,
  className,
  disabled,
  type = 'button',
  ...rest
}: IconButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const text = tooltip ?? label;
  return (
    <>
      <button
        ref={ref}
        type={type}
        aria-label={label}
        className={['btn', 'fe-icon-btn', variantClass[variant], size === 'sm' && 'btn-sm', className]
          .filter(Boolean)
          .join(' ')}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? <Spinner size='sm'>{''}</Spinner> : <FontAwesomeIcon icon={icon} />}
      </button>
      {/* React 19 ignores reactstrap Tooltip's defaultProps, so trigger and autohide are passed here.
          The cast: reactstrap's types predate React 19's nullable RefObject. */}
      <UncontrolledTooltip
        placement={tooltipPosition}
        trigger='hover focus'
        autohide
        target={ref as RefObject<HTMLElement>}
        delay={{ show: 250, hide: 0 }}
        offset={[0, 8]}
      >
        {text}
      </UncontrolledTooltip>
    </>
  );
};
